import { useMutation, useQueryClient } from '@tanstack/react-query';

import { closeDialog, handoff } from '../../api/dialogs';
import { ApiError } from '../../api/client';
import type { DialogDto } from '../../api/types';
import { Button } from '../ui/button';
import { useToast } from '../ui/toast';

export function HandoffActions({ dialog }: { dialog: DialogDto }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const detailKey = ['dialogs', 'detail', dialog.id];

  function useHandoffMutation(apply: () => Promise<DialogDto>, optimisticState: DialogDto['state']) {
    return useMutation({
      mutationFn: apply,
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: detailKey });
        const previous = queryClient.getQueryData<DialogDto>(detailKey);
        if (previous) queryClient.setQueryData<DialogDto>(detailKey, { ...previous, state: optimisticState });
        return { previous };
      },
      onError: (err, _vars, context) => {
        if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
        toast.push(err instanceof ApiError ? err.message : 'Действие не выполнено');
      },
      onSuccess: (updated) => {
        queryClient.setQueryData(detailKey, updated);
        void queryClient.invalidateQueries({ queryKey: ['dialogs', 'list'] });
      },
    });
  }

  const takeOver = useHandoffMutation(() => handoff(dialog.id, 'human'), 'HUMAN_ACTIVE');
  const releaseContinue = useHandoffMutation(() => handoff(dialog.id, 'agent', 'continue'), 'AGENT_ACTIVE');
  const releaseClose = useHandoffMutation(() => handoff(dialog.id, 'agent', 'close'), 'CLOSED');
  const close = useHandoffMutation(() => closeDialog(dialog.id), 'CLOSED');

  const pending = takeOver.isPending || releaseContinue.isPending || releaseClose.isPending || close.isPending;

  return (
    <div className="flex flex-wrap gap-2 border-b border-border p-3">
      {dialog.state === 'AGENT_ACTIVE' && (
        <Button size="sm" disabled={pending} onClick={() => takeOver.mutate()}>
          Взять в работу
        </Button>
      )}
      {dialog.state === 'HUMAN_ACTIVE' && (
        <>
          <Button size="sm" variant="secondary" disabled={pending} onClick={() => releaseContinue.mutate()}>
            Вернуть агенту (продолжить)
          </Button>
          <Button size="sm" variant="secondary" disabled={pending} onClick={() => releaseClose.mutate()}>
            Вернуть агенту (закрыть)
          </Button>
        </>
      )}
      {dialog.state !== 'CLOSED' && (
        <Button size="sm" variant="danger" disabled={pending} onClick={() => close.mutate()}>
          Закрыть
        </Button>
      )}
    </div>
  );
}
